import { createHash, randomUUID } from 'crypto';
import { execSync } from 'child_process';
import { networkInterfaces, hostname, platform, arch, cpus, totalmem } from 'os';
import { join } from 'path';
import fs from 'fs-extra';
import { getSmartStackDir } from './license-paths.js';

export interface MachineIdentity {
  fingerprint: string;
  machineName: string;
  instanceId: string;
}

/**
 * Get stable MAC addresses (exclude virtual/random interfaces)
 */
function getStableMacAddresses(): string[] {
  const interfaces = networkInterfaces();
  const macs: string[] = [];

  for (const [name, addrs] of Object.entries(interfaces)) {
    if (!addrs) continue;
    // Skip virtual interfaces
    if (/^(veth|docker|br-|virbr|lo|vEthernet|isatap|Teredo)/i.test(name)) continue;

    for (const addr of addrs) {
      if (addr.mac && addr.mac !== '00:00:00:00:00:00' && !addr.internal) {
        macs.push(addr.mac);
      }
    }
  }

  return macs.sort();
}

/**
 * Get platform-specific hardware identifier (best-effort)
 */
function getPlatformId(): string {
  const os = platform();
  try {
    if (os === 'win32') {
      const output = execSync('wmic csproduct get uuid', {
        encoding: 'utf-8',
        timeout: 5000,
        stdio: ['pipe', 'pipe', 'pipe'],
      });
      const uuid = output.split('\n').map(l => l.trim()).filter(l => l && l !== 'UUID')[0];
      if (uuid) return uuid;
    } else if (os === 'linux') {
      const machineId = fs.readFileSync('/etc/machine-id', 'utf-8').trim();
      if (machineId) return machineId;
    } else if (os === 'darwin') {
      const output = execSync(
        "ioreg -rd1 -c IOPlatformExpertDevice | awk '/IOPlatformUUID/ { print $3 }'",
        { encoding: 'utf-8', timeout: 5000, stdio: ['pipe', 'pipe', 'pipe'] },
      );
      const uuid = output.trim().replace(/"/g, '');
      if (uuid) return uuid;
    }
  } catch {
    // Silently fall through — platform ID is a bonus, not required
  }
  return '';
}

/**
 * Generate a deterministic machine fingerprint.
 * Combines hardware-stable signals into a SHA-256 hash.
 */
export function generateMachineFingerprint(): string {
  const cpu = cpus()[0];
  const components = [
    platform(),
    arch(),
    cpu?.model || 'unknown-cpu',
    String(totalmem()),
    ...getStableMacAddresses(),
    getPlatformId(),
  ];

  return createHash('sha256').update(components.join('|')).digest('hex');
}

/**
 * Get or create a persistent instanceId for this installation.
 * Stored in ~/.smartstack/device.json
 */
async function getOrCreateInstanceId(): Promise<string> {
  const deviceFile = join(getSmartStackDir(), 'device.json');

  if (await fs.pathExists(deviceFile)) {
    try {
      const data = await fs.readJSON(deviceFile);
      if (data?.instanceId) return data.instanceId;
    } catch {
      // Corrupted file — regenerate
    }
  }

  const instanceId = randomUUID();
  await fs.ensureDir(getSmartStackDir());
  await fs.writeJSON(deviceFile, { instanceId, createdAt: new Date().toISOString() }, { spaces: 2 });
  return instanceId;
}

/**
 * Get the full machine identity (fingerprint + hostname + instanceId)
 */
export async function getMachineIdentity(): Promise<MachineIdentity> {
  const [fingerprint, instanceId] = await Promise.all([
    Promise.resolve(generateMachineFingerprint()),
    getOrCreateInstanceId(),
  ]);

  return {
    fingerprint,
    machineName: hostname(),
    instanceId,
  };
}
